import type { ReactNode } from "react";
import i18n from "@/lib/i18n";
import { getLanguageCode } from "@/lib/direction";

type UnknownError = unknown;

type ErrorToastProps = {
  title?: ReactNode;
  description?: ReactNode;
  variant?: string;
  [key: string]: unknown;
};

const ERROR_PATTERNS: Array<[RegExp, string]> = [
  [/invalid email|valid email|email format/i, "errors.invalidEmail"],
  [/email address not found|sign up again/i, "errors.emailNotFound"],
  [/failed to resend verification/i, "errors.resendVerificationFailed"],
  [/invalid verification link|no token provided/i, "errors.invalidVerificationLink"],
  [/verification error|error.*verification/i, "errors.verificationError"],
  [/verification failed|email verification failed/i, "errors.verificationFailed"],
  [/password reset|reset password|reset link/i, "errors.passwordResetFailed"],
  [/passwords do not match/i, "auth.passwordsMismatch"],
  [/password must be at least/i, "auth.passwordTooShort"],
  [/uppercase letter/i, "auth.passwordNeedsUppercase"],
  [/at least one number/i, "auth.passwordNeedsNumber"],
  [/special character/i, "auth.passwordNeedsSpecial"],
  [/security verification|captcha|turnstile/i, "errors.securityVerification"],
  [/required|fill in all required fields|field is required/i, "errors.fillRequiredFields"],
  [/failed to upload pdf/i, "errors.pdfUploadFailed"],
  [/failed to upload|upload failed/i, "errors.uploadFailed"],
  [/failed to extract pdf|extracting pdf|pdf extraction|extract data from pdf/i, "errors.pdfExtractFailed"],
  [/valid pdf|invalid file|file type/i, "errors.invalidPdfFile"],
  [/invalid data format|extraction data/i, "errors.invalidExtractedData"],
  [/failed to load profile|unable to load profile/i, "errors.loadProfileFailed"],
  [/failed to load teams|fetch teams/i, "errors.loadTeamsFailed"],
  [/failed to delete team/i, "errors.deleteTeamFailed"],
  [/failed to load team comparison/i, "errors.loadTeamComparisonFailed"],
  [/failed to save strengths|saving strengths/i, "errors.saveStrengthsFailed"],
  [/failed to fetch strengths|fetching strengths|clifton strengths/i, "errors.fetchStrengthsFailed"],
  [/failed to fetch users overview|users overview/i, "errors.fetchUsersFailed"],
  [/failed to fetch user ranks|user ranks/i, "errors.fetchRanksFailed"],
  [/failed to fetch top 10/i, "errors.fetchTop10Failed"],
  [/failed to fetch strengths by domain|strengths by domain/i, "errors.fetchByDomainFailed"],
  [/failed to update email|updating email/i, "errors.updateEmailFailed"],
  [/failed to fetch contents|fetching contents/i, "errors.fetchContentFailed"],
  [/failed to create content|creating content/i, "errors.createContentFailed"],
  [/failed to update content|updating content/i, "errors.updateContentFailed"],
  [/failed to delete content|deleting content/i, "errors.deleteContentFailed"],
  [/failed to generate content|generate content/i, "errors.generateContentFailed"],
  [/failed to generate quiz|generate quiz/i, "errors.generateQuizFailed"],
  [/failed to fetch statistics|fetching statistics/i, "errors.fetchStatisticsFailed"],
  [/failed to send reset email/i, "errors.sendResetEmailFailed"],
  [/failed to get response/i, "errors.failedResponse"],
  [/failed to load video/i, "errors.videoLoadFailed"],
  [/unauthorized|not authorized|authenticat/i, "errors.authentication"],
  [/forbidden/i, "errors.forbidden"],
  [/not found/i, "errors.notFound"],
  [/network|failed to fetch|connection/i, "errors.network"],
  [/request failed/i, "errors.requestFailed"],
  [/something went wrong|an error occurred|unexpected error/i, "errors.generic"],
];

const ERRORISH_PATTERNS = [
  /error/i,
  /failed/i,
  /invalid/i,
  /required/i,
  /unable/i,
  /unauthorized/i,
  /forbidden/i,
  /not found/i,
  /network/i,
  /something went wrong/i,
  /an error occurred/i,
];

export const isArabicLanguage = (language?: string) =>
  getLanguageCode(language || i18n.resolvedLanguage || i18n.language) === "ar";

export const getErrorText = (error: UnknownError): string => {
  if (!error) return "";
  if (typeof error === "string") return error;
  if (error instanceof Error) return error.message;

  if (typeof error === "object") {
    const candidate = error as {
      message?: unknown;
      error?: unknown;
      data?: { message?: unknown; error?: unknown };
      response?: { data?: { message?: unknown; error?: unknown } };
    };

    const values = [
      candidate.response?.data?.message,
      candidate.response?.data?.error,
      candidate.data?.message,
      candidate.data?.error,
      candidate.message,
      candidate.error,
    ];

    const text = values.find((value) => typeof value === "string" && value.trim());
    if (text) return text as string;
  }

  return String(error);
};

export const getErrorTranslationKey = (message: string): string | null => {
  const normalized = message.trim();
  if (!normalized) return null;

  for (const [pattern, key] of ERROR_PATTERNS) {
    if (pattern.test(normalized)) return key;
  }

  return null;
};

export const getLocalizedErrorMessage = (
  error: UnknownError,
  fallbackKey = "errors.generic",
  language?: string,
) => {
  const message = getErrorText(error);
  const translationKey = (message && getErrorTranslationKey(message)) || fallbackKey;

  if (!isArabicLanguage(language)) {
    return message || i18n.t(translationKey, { defaultValue: i18n.t(fallbackKey) });
  }

  const translated = i18n.t(translationKey, { defaultValue: "" });
  return translated || i18n.t(fallbackKey, { defaultValue: "حدث خطأ ما. يرجى المحاولة مرة أخرى." });
};

const shouldLocalizeToast = (props: ErrorToastProps) => {
  if (props.variant === "destructive") return true;

  const values = [props.title, props.description].filter((value): value is string => typeof value === "string");
  return values.some((value) => ERRORISH_PATTERNS.some((pattern) => pattern.test(value)));
};

export const localizeToastProps = <T extends ErrorToastProps>(props: T): T => {
  if (!isArabicLanguage() || !shouldLocalizeToast(props)) return props;

  return {
    ...props,
    title: typeof props.title === "string" ? getLocalizedErrorMessage(props.title) : props.title,
    description:
      typeof props.description === "string"
        ? getLocalizedErrorMessage(props.description)
        : props.description,
  };
};
